import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarMonthTaskBar } from './CalendarMonthTaskBar';
import { getLocalDateKey } from './dateUtils';
import {
    getCalendarTaskLabelSpan, getCalendarTaskLanes, getCalendarTaskSegment, getCalendarTaskTimeLabel, getMonthCells,
    getTasksForDate, getTasksForMonth, orderCalendarTasks,
} from './calendarViewUtils';
import { getFlexibleRange, getTaskColorClass } from './scheduleUtils';
import { PlannerTaskInfo } from './types';

export const isCoveredByRangeLabel = (task: PlannerTaskInfo['task'], date: Date, monthDate: Date) => {
    const { start } = getFlexibleRange(task);
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const weekday = (date.getDay() + 6) % 7;
    for (let daysBack = 1; daysBack <= weekday; daysBack += 1) {
        const previousDate = new Date(date);
        previousDate.setDate(previousDate.getDate() - daysBack);
        if (previousDate < startDay) continue;
        const previousSegment = getCalendarTaskSegment(task, previousDate, monthDate);
        if (!previousSegment.showLabel) continue;

        if (getCalendarTaskLabelSpan(task, previousDate, monthDate) > daysBack) return true;
    }
    return false;
};

interface CalendarMonthProps {
    current: boolean;
    date: Date;
    tasks: PlannerTaskInfo[];
    weekdays: string[];
    onCreateTask: (date: Date) => void;
    onOpenDay?: (date: Date) => void;
    onOpenDayTimeline?: (date: Date) => void;
    onOpenYearTimeline?: (date: Date) => void;
    onOpenTask: (taskInfo: PlannerTaskInfo) => void;
    hoveredTaskId: string | null;
    onTaskHoverChange: (taskId: string | null) => void;
}

export const CalendarMonth: React.FC<CalendarMonthProps> = ({
    current, date, tasks, weekdays, onCreateTask, onOpenDay, onOpenDayTimeline, onOpenYearTimeline, onOpenTask,
    hoveredTaskId, onTaskHoverChange,
}) => {
    const { t, i18n } = useTranslation();
    const todayKey = getLocalDateKey(new Date());
    const monthLabel = date.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
    const label = monthLabel[0].toLocaleUpperCase(i18n.language) + monthLabel.slice(1);
    const taskCount = getTasksForMonth(tasks, date).length;
    const taskLanes = getCalendarTaskLanes(tasks, date);
    return (
        <section className="calendar-month calendar-infinite-period"
            data-calendar-current={current || undefined} data-calendar-date={date.toISOString()}
            data-calendar-label={label}>
            <header className="calendar-section-heading">
                <h2>{onOpenYearTimeline ? <button type="button" className="calendar-section-title"
                    onClick={() => onOpenYearTimeline(date)}>{label}</button> : label}</h2>
                {taskCount > 0 && <span>{taskCount}</span>}
            </header>
            <CalendarMonthTaskBar date={date} tasks={tasks} />
            <div className="calendar-weekdays">{weekdays.map((day) => <span key={day}>{day}</span>)}</div>
            <div className="calendar-grid">{getMonthCells(date).map((day, index) => {
                if (!day) return <div key={`empty-${index}`} className="calendar-day calendar-day-empty" aria-hidden="true" />;
                const dateKey = getLocalDateKey(day);
                const dayTasks = orderCalendarTasks(getTasksForDate(tasks, day));
                const taskByLane = new Map(dayTasks.map((taskInfo) => [
                    taskLanes.get(`${dateKey}-${taskInfo.task.id}`) || 0, taskInfo,
                ]));
                const visibleLaneCount = Math.min(3, Math.max(
                    0,
                    ...Array.from(taskByLane.keys()).map((lane) => lane + 1),
                ));
                const hiddenTaskCount = dayTasks.length - Array.from(taskByLane.keys())
                    .filter((lane) => lane < visibleLaneCount).length;
                return <section key={dateKey}
                    className={`calendar-day ${todayKey === dateKey ? 'is-today' : ''} ${onOpenDay ? 'is-clickable' : ''}`}
                    data-calendar-day={dateKey}
                    onClick={() => onOpenDay?.(day)}>
                    <header>{onOpenDayTimeline ? (
                        <button type="button" className="calendar-day-date-button" onClick={(event) => {
                            event.stopPropagation();
                            onOpenDayTimeline(day);
                        }} aria-label={`${t('dailyPlanner.day')} ${day.getDate()}`}>{day.getDate()}</button>
                    ) : <span>{day.getDate()}</span>}{!onOpenDay && (
                        <button type="button" onClick={(event) => {
                            event.stopPropagation();
                            onCreateTask(day);
                        }} aria-label={t('dailyPlanner.addTask')}>+</button>
                    )}</header>
                    <div className="calendar-day-tasks">{Array.from({ length: visibleLaneCount }, (_, lane) => {
                        const taskInfo = taskByLane.get(lane);
                        if (!taskInfo) {
                            return <span key={`placeholder-${lane}`} className="calendar-task-placeholder" />;
                        }
                        if (isCoveredByRangeLabel(taskInfo.task, day, date)) {
                            return <span key={taskInfo.task.id} className="calendar-task-placeholder" aria-hidden="true" />;
                        }
                        const time = getCalendarTaskTimeLabel(taskInfo.task, day);
                        const segment = getCalendarTaskSegment(taskInfo.task, day, date);
                        const labelSpan = segment.showLabel
                            ? getCalendarTaskLabelSpan(taskInfo.task, day, date)
                            : 1;
                        const labelEnd = new Date(day);
                        labelEnd.setDate(labelEnd.getDate() + labelSpan - 1);
                        const labelEndsRange = labelSpan > 1
                            && getCalendarTaskSegment(taskInfo.task, labelEnd, date)
                                .className.includes('range-end');
                        const labelCompletesRange = labelEndsRange
                            && segment.className.includes('range-start');
                        const taskClassName = [
                            'calendar-task',
                            getTaskColorClass(taskInfo.task),
                            segment.className,
                            labelSpan > 1 ? 'range-label-span' : '',
                            labelCompletesRange ? 'range-label-complete' : '',
                            labelEndsRange && !labelCompletesRange ? 'range-label-end' : '',
                            labelSpan > 1 && !labelEndsRange ? 'range-label-continues' : '',
                            taskInfo.task.completed ? 'completed' : '',
                            hoveredTaskId === taskInfo.task.id ? 'is-range-hovered' : '',
                        ].filter(Boolean).join(' ');
                        return <button key={taskInfo.task.id} type="button"
                            className={taskClassName}
                            data-calendar-range-span={labelSpan > 1 ? labelSpan : undefined}
                            data-timeline-task-id={taskInfo.task.id}
                            aria-label={taskInfo.task.title || taskInfo.task.task}
                            onMouseEnter={() => onTaskHoverChange(taskInfo.task.id)}
                            onMouseLeave={() => onTaskHoverChange(null)}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (onOpenDay) onOpenDay(day);
                                else onOpenTask(taskInfo);
                            }}>
                            {segment.showLabel && <>{taskInfo.task.title || taskInfo.task.task}
                                {time && <span>· {time}</span>}</>}
                        </button>;
                    })}{hiddenTaskCount > 0 && <span className="calendar-more">+{hiddenTaskCount}</span>}</div>
                </section>;
            })}</div>
        </section>
    );
};
